import { useEffect } from "react";
import {
  buildHreflangLinks,
  buildLocalizedUrl,
  DEFAULT_SEO_IMAGE,
  ensureAbsoluteImageUrl,
  LANGUAGE_LOCALES,
  SEO_LANGUAGES,
  SITE_NAME,
} from "./seoConfig";

const compactText = (value, maxLength = 190) => {
  const text = String(value || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const upsertMetaByName = (name, content) => {
  let element = document.head.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
  element.dataset.mapapetSeo = "true";
};

const upsertMetaByProperty = (property, content) => {
  let element = document.head.querySelector(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
  element.dataset.mapapetSeo = "true";
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
  element.dataset.mapapetSeo = "true";
};

const replaceHreflangLinks = (links) => {
  document.head
    .querySelectorAll('link[data-mapapet-seo-hreflang="true"]')
    .forEach((element) => element.remove());

  links.forEach(({ language, href }) => {
    const element = document.createElement("link");
    element.setAttribute("rel", "alternate");
    element.setAttribute("hreflang", language);
    element.setAttribute("href", href);
    element.dataset.mapapetSeoHreflang = "true";
    document.head.appendChild(element);
  });
};

const replaceOpenGraphLocaleAlternates = (
  currentLanguage,
  alternateLanguages
) => {
  document.head
    .querySelectorAll('meta[data-mapapet-seo-og-alternate="true"]')
    .forEach((element) => element.remove());

  (Array.isArray(alternateLanguages)
    ? alternateLanguages
    : SEO_LANGUAGES)
    .filter((language) => language !== currentLanguage)
    .forEach((language) => {
      const locale = LANGUAGE_LOCALES[language];
      if (!locale) return;

      const element = document.createElement("meta");
      element.setAttribute("property", "og:locale:alternate");
      element.setAttribute("content", locale);
      element.dataset.mapapetSeoOgAlternate = "true";
      document.head.appendChild(element);
    });
};

const replaceJsonLd = (jsonLd) => {
  document.head
    .querySelectorAll('script[data-mapapet-seo-jsonld="true"]')
    .forEach((element) => element.remove());

  if (!jsonLd) return;

  const element = document.createElement("script");
  element.setAttribute("type", "application/ld+json");
  element.dataset.mapapetSeoJsonld = "true";
  element.textContent = JSON.stringify(jsonLd).replace(/</g, "\\u003c");
  document.head.appendChild(element);
};

export const applySeoTags = ({
  title,
  description,
  language = "en",
  canonicalPath = "/",
  canonicalUrl,
  image = DEFAULT_SEO_IMAGE,
  type = "website",
  robots = "index,follow",
  jsonLd,
  alternateLanguages,
}) => {
  if (typeof document === "undefined") return;

  const safeLanguage = SEO_LANGUAGES.includes(language) ? language : "en";
  const safeTitle = compactText(title || SITE_NAME, 75) || SITE_NAME;
  const safeDescription = compactText(description, 190);
  const safeCanonicalUrl = canonicalUrl || buildLocalizedUrl(
    safeLanguage,
    canonicalPath
  );
  const safeImage = ensureAbsoluteImageUrl(image);
  const hreflangLinks = Array.isArray(alternateLanguages)
    ? [
        ...alternateLanguages.map((alternateLanguage) => ({
          language: alternateLanguage,
          href: buildLocalizedUrl(alternateLanguage, canonicalPath),
        })),
        ...(alternateLanguages.length > 0
          ? [{
              language: "x-default",
              href: buildLocalizedUrl(
                alternateLanguages.includes("en") ? "en" : safeLanguage,
                canonicalPath
              ),
            }]
          : []),
      ]
    : buildHreflangLinks(canonicalPath);

  document.title = safeTitle;

  upsertMetaByName("description", safeDescription);
  upsertMetaByName("robots", robots);
  upsertMetaByName("googlebot", robots);
  upsertMetaByName("twitter:card", "summary_large_image");
  upsertMetaByName("twitter:title", safeTitle);
  upsertMetaByName("twitter:description", safeDescription);
  upsertMetaByName("twitter:image", safeImage);

  upsertMetaByProperty("og:site_name", SITE_NAME);
  upsertMetaByProperty("og:type", type);
  upsertMetaByProperty("og:title", safeTitle);
  upsertMetaByProperty("og:description", safeDescription);
  upsertMetaByProperty("og:url", safeCanonicalUrl);
  upsertMetaByProperty("og:image", safeImage);
  upsertMetaByProperty("og:image:alt", safeTitle);
  upsertMetaByProperty(
    "og:locale",
    LANGUAGE_LOCALES[safeLanguage] || LANGUAGE_LOCALES.en
  );

  upsertCanonical(safeCanonicalUrl);
  replaceHreflangLinks(hreflangLinks);
  replaceOpenGraphLocaleAlternates(
    safeLanguage,
    alternateLanguages
  );
  replaceJsonLd(jsonLd);
};

const SeoHead = (props) => {
  const {
    title,
    description,
    language,
    canonicalPath,
    canonicalUrl,
    image,
    type,
    robots,
    jsonLd,
    alternateLanguages,
  } = props;

  useEffect(() => {
    applySeoTags({
      title,
      description,
      language,
      canonicalPath,
      canonicalUrl,
      image,
      type,
      robots,
      jsonLd,
      alternateLanguages,
    });
  }, [
    alternateLanguages,
    canonicalPath,
    canonicalUrl,
    description,
    image,
    jsonLd,
    language,
    robots,
    title,
    type,
  ]);

  return null;
};

export default SeoHead;
